import { AppError } from '@cetused/shared'

/**
 * The POST-AUTH DATA stack — the heavy steps the SHELL and the /login page must
 * never depend on: the device keyring, the encrypted storage gateway, state
 * hydration, RBAC, lanes, and the audit trail. They exist for STUDENT DATA and
 * are lazy-initialized on FIRST ENTRY into an authenticated data route, never at
 * app boot (see ./index.ts for why the shell boot no longer runs them).
 *
 * ⛔2 FAIL CLOSED — this is NOT boot tolerance leaking into "data works".
 * Today the storage gateway and keyring are honest refusals (TASK-00066), so
 * ensureDataStack() REJECTS on the storage step. A data route that awaits it
 * MUST render an honest "not available yet" and MUST NOT render student data
 * (ui/data/DataStackBoundary enforces exactly that). Moving these steps off the
 * boot path let the login page render; it did not make the data stack work. The
 * refusal still fires — it fires here, when a data path uses it, and it is the
 * same refusal the gateway has always thrown.
 *
 * ⛔4 NO FABRICATED SUCCESS — this resolves ONLY if every step resolves. It has
 * no fallback branch that reports readiness it did not achieve; on any step's
 * rejection it rejects, so a caller can never mistake a refused stack for a
 * ready one.
 */

let dataStackPromise: Promise<void> | null = null

async function initializeDataStack(): Promise<void> {
  // Keyring initialization (PBKDF2 + AES-256-GCM). Owned by TASK-00064.
  await import('@core/crypto/keyring').then((m) => m.initializeKeyring())

  // Storage layer (IndexedDB + localStorage gateway) — REFUSES today
  // (TASK-00066/TASK-00037). This is the step that fails closed until the
  // encrypted store is implemented; nothing below it runs while it refuses.
  await import('@core/storage/gateway').then((m) => m.initializeStorage())

  // State root hydration (load persisted S from storage).
  await import('@core/state').then((m) => m.hydrateState())

  // RBAC capabilities + scope resolution.
  await import('@core/rbac/gateway').then((m) => m.initializeRBAC())

  // Lane registry resolution.
  await import('@registries/lanes').then((m) => m.resolveLanes())

  // Crypto audit trail readiness — via the browser-safe entry (the node:crypto
  // hash chain cannot bundle for the browser). NOTE: not wired yet; append()
  // refuses every entry (fail closed) until persistence lands.
  await import('@cetused/platform-audit/browser').then((m) => m.initializeAudit())
}

/**
 * Single-flight: initialize the data stack at most once per successful attempt.
 * On failure the memoized promise is CLEARED so a later data-route entry retries
 * once the stack is actually implemented — but until then every call REJECTS.
 * It never resolves on a refusing gateway.
 */
export function ensureDataStack(): Promise<void> {
  if (dataStackPromise) return dataStackPromise
  dataStackPromise = initializeDataStack().catch((err: unknown) => {
    // Clear the memo so the next data-route entry re-attempts rather than
    // caching a stale failure; rethrow so the caller fails closed. This does
    // NOT swallow the error — it is handled (memo reset) AND rethrown.
    dataStackPromise = null
    throw err instanceof AppError
      ? err
      : AppError.internal('Data stack initialization failed')
  })
  return dataStackPromise
}

/**
 * Test/teardown hook: forget any in-flight or resolved data-stack attempt so a
 * fresh ensureDataStack() re-runs from clean. Not used by the app at runtime.
 */
export function resetDataStackForTest(): void {
  dataStackPromise = null
}
